import { useEffect } from "react";
import { X, Check, CheckCheck, Clock, Send, Truck, Eye, Pencil, Forward, FileText } from "lucide-react";

const fmtLocal = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    weekday: "short", day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

const fmtUtc = (value) => {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toUTCString();
};

const STATUS_META = {
  SENT:      { label: "Sent",      icon: Check,     cls: "text-text_muted" },
  DELIVERED: { label: "Delivered", icon: CheckCheck, cls: "text-text_muted" },
  READ:      { label: "Seen",      icon: CheckCheck, cls: "text-info" },
  SENDING:   { label: "Sending",   icon: Clock,     cls: "text-text_muted" },
};

const Row = ({ icon: Icon, label, primary, secondary, accent }) => (
  <div className="flex items-start gap-3 px-1 py-2.5 border-b border-border last:border-b-0">
    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0
      ${accent ? "bg-info_bg text-info" : "bg-surface_alt text-text_secondary"}`}>
      <Icon size={15} />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[11px] font-medium uppercase tracking-wide text-text_muted">{label}</p>
      <p className="text-[13px] text-text_primary leading-snug">{primary}</p>
      {secondary && <p className="text-[11px] text-text_muted mt-0.5">{secondary}</p>}
    </div>
  </div>
);

const MessageInfoModal = ({ message, isOwn, onClose }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const status = message.sending ? "SENDING" : (message.status || "SENT");
  const sm = STATUS_META[status] || STATUS_META.SENT;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" onClick={onClose} />

      <div className="relative w-full max-w-[420px] bg-surface border border-border rounded-2xl
        overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.2)] slide-in">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-border bg-surface_alt">
          <div className="flex items-center gap-2">
            <sm.icon size={17} className={sm.cls} />
            <h3 className="text-[15px] font-semibold text-text_primary">Message info</h3>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-text_muted
              hover:bg-surface hover:text-text_primary transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Message preview */}
        <div className="px-5 pt-4">
          <div className="px-3.5 py-2.5 rounded-xl bg-surface_alt border border-border text-[12.5px]
            text-text_primary max-h-24 overflow-y-auto custom-scrollbar">
            {message.is_deleted ? (
              <span className="italic text-text_muted">This message was deleted</span>
            ) : message.message ? (
              <span className="whitespace-pre-wrap break-words">{message.message}</span>
            ) : message.message_attachments?.length ? (
              <span className="flex items-center gap-1.5 text-text_secondary">
                <FileText size={13} /> {message.message_attachments[0].file_name}
              </span>
            ) : (
              <span className="italic text-text_muted">No text content</span>
            )}
          </div>
        </div>

        {/* Details */}
        <div className="px-5 py-3">
          <div className="flex items-center gap-2 px-1 pb-1.5">
            <span className={`text-[12px] font-semibold ${sm.cls}`}>{sm.label}</span>
            {message.is_edited && (
              <span className="flex items-center gap-1 text-[10.5px] text-text_muted">
                <Pencil size={10} /> edited
              </span>
            )}
            {message.is_forwarded && (
              <span className="flex items-center gap-1 text-[10.5px] text-text_muted">
                <Forward size={10} /> forwarded
              </span>
            )}
          </div>

          <Row
            icon={Send}
            label="Sent"
            primary={fmtLocal(message.created_date || message.timestamp) || "—"}
            secondary={fmtUtc(message.created_date || message.timestamp) ? `UTC: ${fmtUtc(message.created_date || message.timestamp)}` : null}
          />

          {/* Delivered/Seen are most meaningful for your own outgoing messages */}
          {isOwn && (
            <Row
              icon={Truck}
              label="Delivered"
              primary={fmtLocal(message.delivered_date) || "Not delivered yet"}
            />
          )}

          {isOwn && (
            <Row
              icon={Eye}
              label="Seen"
              primary={fmtLocal(message.readed_date) || (message.status === "READ" ? "Seen" : "Not seen yet")}
              accent={!!message.readed_date}
            />
          )}

          {message.is_edited && (
            <Row
              icon={Pencil}
              label="Last edited"
              primary={fmtLocal(message.updated_date) || "—"}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageInfoModal;
